import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { YoutubeComparePosts } from '@anytag/graphql';
import { HelpIcon, MediaPreviewWithMetrics } from '@anytag/shared-atoms';
import { THEME } from '@anytag/shared-constants';
import { usePostDetailsParam, useSelectItem } from '@anytag/shared-hooks';
import { InfluencerYoutubeAnalyticsPostDetailsModal } from '@anytag/shared-molecules';
import { bigIntFormatter } from '@anytag/shared-utils';
import { PostDetailsType } from '@anytag/shared-types';
import {
  AnalyticsComparePostsHabit,
  AnalyticsComparePostsTable,
  AnalyticsComparePostsSlider,
  AnalyticsComparePostsAccount,
  AnalyticsComparePostsHabitModal,
  AnalyticsComparePostsHabitModalProps
} from '../../shared';
import { AnalyticsYoutubeComparableAccountType } from '../types';

interface TemplateProps {
  data: YoutubeComparePosts;
  comparableAccounts: AnalyticsYoutubeComparableAccountType[];
}

export const Template = ({ data, comparableAccounts }: TemplateProps) => {
  const { t } = useTranslation();
  const { setPostDetailsParam } = usePostDetailsParam();
  const {
    setSelectedItem: setPostingHabit,
    selectedItem: selectedPostingHabit,
    resetSelectedItem: resetPostingHabit
  } = useSelectItem<Pick<AnalyticsComparePostsHabitModalProps, 'account' | 'data'>>();

  const composeData = [
    data.mainAccount,
    ...(data.compareAccounts.filter((account) => account.accountId !== data.mainAccount.accountId) || [])
  ];
  const tableData = comparableAccounts.map((account) => ({
    account,
    ...composeData.find((item) => item.accountId === account.id)
  }));
  const columns: ColumnDef<(typeof tableData)[number]>[] = [
    {
      size: 160,
      accessorKey: 'account',
      header: t<string>('HeaderColumn.Account'),
      cell: ({ row }) => (
        <AnalyticsComparePostsAccount
          account={{
            name: row.original.account.username,
            avatar: row.original.account.avatarUrl
          }}
        />
      )
    },
    {
      size: 2000, // take all the space
      accessorKey: 'postingHabit',
      header: () => (
        <div className="flex flex-[1.2] items-center pr-4 pl-2">
          <HelpIcon className="mr-1" help={t('Tooltip.comparePostingHabits')} />
          {t('HeaderColumn.Posting Habits')}
        </div>
      ),
      cell: ({ row }) => (
        <>
          <div className="box-border py-3">
            <AnalyticsComparePostsHabit
              postsHabit={row.original.postHabit || []}
              css={{ flex: 1.2, paddingTop: '4px', paddingBottom: '4px', borderRight: THEME.border.base }}
              buttonProps={{
                onClick: () =>
                  row.original.account && row.original.postHabit
                    ? setPostingHabit({
                        data: row.original.postHabit,
                        account: { name: row.original.account.username, avatar: row.original.account.avatarUrl }
                      })
                    : undefined
              }}
            />
          </div>

          <AnalyticsComparePostsSlider
            css={{ minHeight: '209px' }}
            posts={row.original.posts || []}
            renderPost={(post) => (
              <MediaPreviewWithMetrics
                objectFit="cover"
                title={post.title}
                src={post.thumbnail}
                css={THEME.mediaPreview.hasCaptionOrTitle}
                onClick={() => setPostDetailsParam?.(post.id)}
                statistics={[
                  { label: t('View'), value: bigIntFormatter(post.views, 0, { k: true, m: true }) },
                  { label: t('Like'), value: bigIntFormatter(post.likes, 0, { k: true, m: true }) },
                  { label: t('Comment'), value: bigIntFormatter(post.comments, 0, { k: true, m: true }) }
                ]}
              />
            )}
          />

          <InfluencerYoutubeAnalyticsPostDetailsModal
            posts={
              row.original.posts?.map(
                ({ id }): PostDetailsType => ({ id: String(id), mainSocialAccountId: row.original.account.id })
              ) || []
            }
            isAnalytics
          />
        </>
      )
    }
  ];

  return (
    <>
      <AnalyticsComparePostsTable data={tableData} columns={columns} />

      <AnalyticsComparePostsHabitModal
        onClose={resetPostingHabit}
        open={!!selectedPostingHabit}
        {...selectedPostingHabit}
      />
    </>
  );
};
